// M4 Custody & Transfer — Workshop "Prepare & Pack" (supply shell, D-7).
//
// Operational simulation of the Hedi2 workshop: create already-labeled customer
// packages, scan them into a master container (live scanned/expected counter),
// seal (count-gate enforced server-side), then hand off (depart). The custody
// timeline reflects the append-only event ledger. MSW in maket mode; real
// backend (gated by settings.custody_enabled) after cutover.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Boxes, ScanLine, Lock, Send } from 'lucide-react'
import {
  type CustodyContainer,
  type CustodyPackage,
  type CustodyTransfer,
  createContainer,
  createNode,
  createPackages,
  createTransfer,
  departTransfer,
  listEvents,
  scanIntoContainer,
  sealContainer,
} from '../../api/custody'
import { useT } from '../../i18n'
import { SUPPLY_PREPARE_PACK_STRINGS } from '../../i18n/supply/prepare-pack'

function genQr(): string {
  return `QR-${Math.random().toString(36).slice(2, 12).toUpperCase()}`
}

export function WorkshopPreparePack() {
  const t = useT(SUPPLY_PREPARE_PACK_STRINGS)
  const qc = useQueryClient()

  const [count, setCount] = useState(10)
  const [transfer, setTransfer] = useState<CustodyTransfer | null>(null)
  const [container, setContainer] = useState<CustodyContainer | null>(null)
  const [packages, setPackages] = useState<CustodyPackage[]>([])
  const [scannedIds, setScannedIds] = useState<Set<string>>(new Set())
  const [master, setMaster] = useState('')
  const [error, setError] = useState<string | null>(null)

  const eventsQ = useQuery({
    queryKey: ['custody', 'events', 'transfer', transfer?.id],
    queryFn: () => listEvents('transfer', transfer!.id),
    enabled: !!transfer,
  })

  const refetchEvents = () =>
    qc.invalidateQueries({ queryKey: ['custody', 'events', 'transfer', transfer?.id] })

  const setup = useMutation({
    mutationFn: async (n: number) => {
      const origin = await createNode({ node_kind: 'workshop_vendor', name: 'TR workshop' })
      const dest = await createNode({ node_kind: 'hub', name: 'US hub' })
      const tr = await createTransfer({
        transfer_kind: 'consolidation_export',
        origin_node_id: origin.id,
        dest_node_id: dest.id,
        expected_unit_count: n,
        feature_flow: 'hedi2_tr_us',
      })
      const pkgs = await createPackages(
        Array.from({ length: n }, () => ({
          nature: 'order_bound',
          order_id: crypto.randomUUID(),
          internal_qr: genQr(),
          last_mile_tracking_number: `9400${Math.random().toString().slice(2, 12)}`,
        })),
      )
      const cnt = await createContainer({
        container_kind: 'master_carton',
        transfer_id: tr.id,
        expected_child_count: n,
      })
      return { tr, pkgs, cnt }
    },
    onSuccess: ({ tr, pkgs, cnt }) => {
      setError(null)
      setTransfer(tr)
      setPackages(pkgs)
      setContainer(cnt)
      setScannedIds(new Set())
      void refetchEvents()
    },
    onError: (e: Error) => setError(e.message),
  })

  const scan = useMutation({
    mutationFn: (pkgId: string) => scanIntoContainer(container!.id, pkgId),
    onSuccess: (cnt, pkgId) => {
      setError(null)
      setContainer(cnt)
      setScannedIds((prev) => new Set(prev).add(pkgId))
      void refetchEvents()
    },
    onError: (e: Error) => setError(e.message),
  })

  const seal = useMutation({
    mutationFn: () => sealContainer(container!.id),
    onSuccess: (cnt) => {
      setError(null)
      setContainer(cnt)
      void refetchEvents()
    },
    onError: (e: Error) => setError(e.message),
  })

  const depart = useMutation({
    mutationFn: () => departTransfer(transfer!.id, { master_tracking_number: master || undefined }),
    onSuccess: (tr) => {
      setError(null)
      setTransfer(tr)
      void refetchEvents()
    },
    onError: (e: Error) => setError(e.message),
  })

  const scanned = container?.scanned_child_count ?? 0
  const expected = container?.expected_child_count ?? 0
  const allScanned = expected > 0 && scanned === expected
  const isSealed = container?.status === 'sealed'
  const isDeparted = transfer?.status === 'in_transit'

  const reset = () => {
    setTransfer(null)
    setContainer(null)
    setPackages([])
    setScannedIds(new Set())
    setMaster('')
    setError(null)
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Boxes className="h-6 w-6" /> {t.pageTitle}
        </h1>
        <p className="text-sm text-[var(--color-text-muted)]">{t.subtitle}</p>
      </header>

      {error && (
        <div className="rounded-md border border-[var(--color-danger)] bg-[var(--color-surface-muted)] px-4 py-2 text-sm text-[var(--color-danger)]">
          {error}
        </div>
      )}

      {/* Step 1 — create packages + container */}
      <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <h2 className="mb-3 font-medium">{t.step1}</h2>
        {!transfer ? (
          <div className="flex items-end gap-3">
            <label className="text-sm">
              <span className="mb-1 block text-[var(--color-text-muted)]">{t.packageCount}</span>
              <input
                type="number"
                min={1}
                max={500}
                value={count}
                onChange={(e) => setCount(Math.max(1, Number(e.target.value) || 1))}
                className="w-28 rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-1.5 text-[var(--color-text)] focus-visible:shadow-[var(--shadow-focus)]"
              />
            </label>
            <button
              onClick={() => setup.mutate(count)}
              disabled={setup.isPending}
              className="rounded-md bg-[var(--color-action)] px-4 py-2 text-sm font-medium text-[var(--color-action-foreground)] disabled:opacity-50"
            >
              {t.createPackages}
            </button>
          </div>
        ) : (
          <p className="text-sm text-[var(--color-success)]">{t.packagesReady(packages.length)}</p>
        )}
      </section>

      {/* Step 2 — scan into container */}
      {transfer && container && (
        <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-medium">
              <ScanLine className="h-5 w-5" /> {t.step2}
            </h2>
            <span
              className={`rounded-full px-3 py-1 text-sm font-medium ${
                allScanned ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
              }`}
            >
              {t.scanned(scanned, expected)}
            </span>
          </div>
          <ul className="divide-y divide-[var(--color-border)]">
            {packages.map((p) => {
              const done = scannedIds.has(p.id)
              return (
                <li key={p.id} className="flex items-center justify-between py-2 text-sm">
                  <span className="font-mono text-[var(--color-text-muted)]">{p.internal_qr}</span>
                  {done ? (
                    <span className="text-[var(--color-success)]">✓</span>
                  ) : (
                    <button
                      onClick={() => scan.mutate(p.id)}
                      disabled={scan.isPending || isSealed}
                      className="rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-1 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-muted)] disabled:opacity-50"
                    >
                      {t.scanIn}
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        </section>
      )}

      {/* Step 3 — seal */}
      {transfer && container && (
        <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h2 className="mb-3 flex items-center gap-2 font-medium">
            <Lock className="h-5 w-5" /> {t.step3}
          </h2>
          {isSealed ? (
            <p className="text-sm font-medium text-[var(--color-success)]">{t.sealed}</p>
          ) : (
            <div className="space-y-2">
              <button
                onClick={() => seal.mutate()}
                disabled={!allScanned || seal.isPending}
                className="rounded-md bg-[var(--color-action)] px-4 py-2 text-sm font-medium text-[var(--color-action-foreground)] disabled:opacity-50"
              >
                {t.seal}
              </button>
              {!allScanned && <p className="text-xs text-[var(--color-text-muted)]">{t.sealBlocked}</p>}
            </div>
          )}
        </section>
      )}

      {/* Step 4 — depart */}
      {isSealed && transfer && (
        <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h2 className="mb-3 flex items-center gap-2 font-medium">
            <Send className="h-5 w-5" /> {t.step4}
          </h2>
          {isDeparted ? (
            <p className="text-sm font-medium text-[var(--color-success)]">{t.departed}</p>
          ) : (
            <div className="flex items-end gap-3">
              <label className="text-sm">
                <span className="mb-1 block text-[var(--color-text-muted)]">{t.masterTracking}</span>
                <input
                  value={master}
                  onChange={(e) => setMaster(e.target.value)}
                  className="w-56 rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-1.5 text-[var(--color-text)] focus-visible:shadow-[var(--shadow-focus)]"
                  placeholder="1Z…"
                />
              </label>
              <button
                onClick={() => depart.mutate()}
                disabled={depart.isPending}
                className="rounded-md bg-[var(--color-action)] px-4 py-2 text-sm font-medium text-[var(--color-action-foreground)] disabled:opacity-50"
              >
                {t.depart}
              </button>
            </div>
          )}
        </section>
      )}

      {/* Timeline */}
      {transfer && (
        <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-medium">{t.timeline}</h2>
            <button onClick={reset} className="text-xs text-[var(--color-text-muted)] underline">
              {t.reset}
            </button>
          </div>
          {eventsQ.data && eventsQ.data.items.length > 0 ? (
            <ul className="space-y-1 text-sm">
              {eventsQ.data.items.map((e) => (
                <li key={e.id} className="flex justify-between text-[var(--color-text-muted)]">
                  <span className="font-mono">{e.event_type}</span>
                  <span className="text-[var(--color-text-muted)]">{e.subject_kind}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[var(--color-text-muted)]">{t.empty}</p>
          )}
        </section>
      )}
    </div>
  )
}
